import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { SalesShell } from "@/components/crm/SalesShell";
import { EmptyState, SelectField, Stat } from "@/components/crm/parts";
import { useAccounts } from "@/lib/crm/useCrm";
import {
  customerResolver,
  useCustomerWork,
  type AccountRef,
  type CustomerTask,
} from "@/lib/crm/customer-work";
import { DUE_BANDS, dueBand, formatDue, round1, taskWeeklyTotal } from "@/lib/hub";

export const Route = createFileRoute("/_authenticated/sales/customer-tasks")({
  head: () => ({
    meta: [
      { title: "Customer tasks — MyPriorities" },
      {
        name: "description",
        content:
          "Open and completed work grouped by the customer account it is linked or tagged to.",
      },
      { property: "og:title", content: "Customer tasks — MyPriorities" },
      {
        property: "og:description",
        content: "Every task that belongs to a customer, with due-date urgency and hours.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: CustomerTasksPage,
});

function CustomerTasksPage() {
  return (
    <SalesShell
      section="customers"
      title="Customer tasks"
      subtitle="Work from your Priorities hub, grouped by the customer it belongs to."
    >
      {({ orgId }) => <CustomerTasks orgId={orgId} />}
    </SalesShell>
  );
}

type Row = {
  account: AccountRef;
  open: CustomerTask[];
  doneCount: number;
  hours: number;
};

function CustomerTasks({ orgId }: { orgId: string }) {
  const accounts = useAccounts(orgId);
  const work = useCustomerWork(orgId);
  const [accountId, setAccountId] = useState("all");
  const [search, setSearch] = useState("");

  const refs = useMemo<AccountRef[]>(
    () => (accounts.data ?? []).map((a) => ({ id: a.id, name: a.name })),
    [accounts.data],
  );
  const resolver = useMemo(() => customerResolver(refs, work.links), [refs, work.links]);

  const rows = useMemo<Row[]>(() => {
    const map = new Map<string, Row>();
    const bucket = (account: AccountRef) => {
      const existing = map.get(account.id);
      if (existing) return existing;
      const created: Row = { account, open: [], doneCount: 0, hours: 0 };
      map.set(account.id, created);
      return created;
    };

    const term = search.trim().toLowerCase();
    for (const task of work.tasks) {
      if (term && !task.title.toLowerCase().includes(term)) continue;
      for (const account of resolver.accountsFor(task)) {
        const row = bucket(account);
        if (task.done) row.doneCount += 1;
        else row.open.push(task);
      }
    }
    for (const done of work.completions) {
      for (const account of resolver.accountsFor(done)) {
        const row = bucket(account);
        row.hours = round1(row.hours + Number(done.hours || 0));
      }
    }

    return [...map.values()]
      .filter((row) => accountId === "all" || row.account.id === accountId)
      .sort((a, b) => b.open.length - a.open.length || a.account.name.localeCompare(b.account.name));
  }, [resolver, work.tasks, work.completions, accountId, search]);

  const openTotal = rows.reduce((sum, row) => sum + row.open.length, 0);
  const hoursTotal = round1(rows.reduce((sum, row) => sum + row.hours, 0));

  if (work.isLoading || accounts.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading customer tasks…</p>;
  }

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Customers with work" value={String(rows.length)} />
        <Stat label="Open tasks" value={String(openTotal)} />
        <Stat label="Hours logged" value={`${hoursTotal}h`} hint="All completed work" />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[12rem]">
          <SelectField
            label="Customer"
            value={accountId}
            onChange={setAccountId}
            options={[
              { value: "all", label: "All customers" },
              ...refs.map((a) => ({ value: a.id, label: a.name })),
            ]}
          />
        </div>
        <div className="min-w-[12rem] flex-1">
          <label className="label-caps" htmlFor="task-search">
            Search
          </label>
          <Input
            id="task-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Task title…"
            className="mt-1"
          />
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title="No customer work found"
          body="Link a task to an account, or tag it with a client name that matches one of your accounts."
        />
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <section key={row.account.id} className="rounded-lg border border-border bg-card p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-display text-base font-semibold">{row.account.name}</h2>
                <p className="text-xs text-muted-foreground">
                  {row.open.length} open · {row.doneCount} done · {row.hours}h logged
                </p>
              </div>
              {row.open.length === 0 ? (
                <p className="mt-2 text-sm text-muted-foreground">Nothing open right now.</p>
              ) : (
                <ul className="mt-2 space-y-1.5">
                  {row.open.map((task) => {
                    const band = dueBand(task.due_date);
                    return (
                      <li key={task.id} className="flex items-center justify-between gap-2 text-sm">
                        <span className="min-w-0 truncate">
                          {task.title}
                          <span className="ml-2 text-xs text-muted-foreground">
                            {task.task_type}
                          </span>
                        </span>
                        <span className="flex shrink-0 items-center gap-2">
                          <span className="text-xs text-muted-foreground">
                            {taskWeeklyTotal(task)}h
                          </span>
                          {task.due_date ? (
                            <Badge className={DUE_BANDS[band].className}>
                              {formatDue(task.due_date)}
                            </Badge>
                          ) : (
                            <Badge variant="outline">No date</Badge>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
